// CarFields — Presentational field group for the "car" event type. Renders the
// controlled inputs the source's `renderCarRentalForm()` produced: Rental
// Company, the optional Car Type, Pickup/Return Location, Pickup/Return Date,
// and the optional Confirmation Number, including the per-field required-error
// display.
//
// Extracted verbatim from `AddEventForm.js` (`renderCarRentalForm`) as part of
// the behavior-preserving refactor (issue #240). The inputs are fully
// controlled by props: values come from `formData`, edits call
// `onChange(field, value)` (the source's `handleInputChange`), and per-field
// errors come from `errors` (the source's `validationErrors`). The two render
// helpers — `renderFieldError` and `getFieldBorderStyle` — are copied verbatim
// from the source and read only the `errors` prop, so the component stays
// render-only with NO internal state. Labels, placeholders, field order, the
// `validation-error-<field>` test ids, styles, and attributes are identical to
// the source.
//
// Props:
//   formData - object; the controlled field values
//   errors   - object; per-field validation error messages (keyed by field)
//   onChange - (field, value) => void invoked on each input change
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';

export const CarFields = React.memo(function CarFields({
  formData,
  errors,
  onChange
}) {
  // Native date inputs have a browser-enforced minimum rendered width
  // (~150px) that two side-by-side columns can't fit on a phone screen —
  // stack to one column below the mobile breakpoint instead of letting the
  // second field overflow the modal.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const renderFieldError = (field) => {
    if (!errors[field]) return null;
    return (
      <span
        data-testid={`validation-error-${field}`}
        style={{
          display: 'block',
          fontSize: '12px',
          color: '#dc2626',
          marginTop: '4px'
        }}
      >
        {errors[field]}
      </span>
    );
  };

  const getFieldBorderStyle = (field) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 12px',
    border: `1px solid ${errors[field] ? '#dc2626' : '#d1d5db'}`,
    borderRadius: '6px',
    fontSize: '14px'
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Rental Company *
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => onChange('company', e.target.value)}
            placeholder="e.g., Hertz, Avis, Enterprise"
            style={getFieldBorderStyle('company')}
          />
          {renderFieldError('company')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Car Type
          </label>
          <input
            type="text"
            value={formData.carType}
            onChange={(e) => onChange('carType', e.target.value)}
            placeholder="e.g., Economy, Mid-size, SUV"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Pickup Location *
          </label>
          <input
            type="text"
            value={formData.pickupLocation}
            onChange={(e) => onChange('pickupLocation', e.target.value)}
            placeholder="e.g., LAX Airport"
            style={getFieldBorderStyle('pickupLocation')}
          />
          {renderFieldError('pickupLocation')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Return Location *
          </label>
          <input
            type="text"
            value={formData.returnLocation}
            onChange={(e) => onChange('returnLocation', e.target.value)}
            placeholder="e.g., LAX Airport"
            style={getFieldBorderStyle('returnLocation')}
          />
          {renderFieldError('returnLocation')}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Pickup Date *
          </label>
          <input
            type="date"
            value={formData.pickupDate}
            onChange={(e) => onChange('pickupDate', e.target.value)}
            style={getFieldBorderStyle('pickupDate')}
          />
          {renderFieldError('pickupDate')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Return Date *
          </label>
          <input
            type="date"
            value={formData.returnDate}
            onChange={(e) => onChange('returnDate', e.target.value)}
            style={getFieldBorderStyle('returnDate')}
          />
          {renderFieldError('returnDate')}
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Confirmation Number
        </label>
        <input
          type="text"
          value={formData.carConfirmationNumber}
          onChange={(e) => onChange('carConfirmationNumber', e.target.value)}
          placeholder="e.g., 123456789"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>
    </div>
  );
});

export default CarFields;
